import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryValidationService } from '../../services/query-validation.service';
import { Injectable } from '@angular/core';
import { ACCOUNTS_ACTIONS } from '../actions/accounts.actions';
import { of, zip } from 'rxjs';
import { SITES_ACTIONS } from '../actions/sites.actions';
import { catchError, first, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { APPLICATION_FILTERS_REDUCER } from '../reducers/filters.reducer';
import {
  APPLICATION_FILTERS_ACTIONS,
  APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES,
  APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES,
} from '../actions/filters.actions';
import { QueryHashService } from '../../services/query-hash.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { RouterService } from '../../services';
import { APP_QUERY_PARAMS } from '../../configurations/router.config';

@Injectable()
export class FiltersEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly queryValidationService: QueryValidationService,
    private readonly hashService: QueryHashService,
    private readonly routerService: RouterService,
  ) {}

  resolveAccountsAndSitesQueries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES.get),
      switchMap(({ data }) =>
        this.store.select(APPLICATION_FILTERS_REDUCER.isValidated).pipe(
          first(),
          map(() => data),
        ),
      ),
      switchMap(({ accountIdsHash, siteIdsHash }) =>
        this.hashService.getAccountsAndSitesByHashes(accountIdsHash, siteIdsHash).pipe(
          switchMap(async (data) => {
            await this.queryValidationService.validateAccountsAndSites(data.accountIds, data.siteIds);
            return APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES.success();
          }),
          catchError(() => {
            this.routerService.navigateByAccountsAndSites(null, null);
            return of(APPLICATION_FILTERS_GET_ACCOUNTS_AND_SITES_QUERIES.error());
          }),
        ),
      ),
    ),
  );

  generateAccountsAndSitesQueriesForCurrentPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES.save),
      withLatestFrom(this.store.select(APPLICATION_FILTERS_REDUCER['selectApp-filtersState'])),
      switchMap(([, { accountIds, siteIds }]) =>
        fromPromise(this.hashService.saveAccountsAndSites(siteIds, accountIds)).pipe(
          map(({ accountIdsHash, siteIdsHash }) => {
            this.onAccountsAndSitesQueriesGenerated(accountIdsHash, siteIdsHash);
            return APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES.success();
          }),
          catchError(() => {
            this.routerService.navigateByAccountsAndSites(null, null);
            return of(APPLICATION_FILTERS_SAVE_ACCOUNTS_AND_SITES_QUERIES.error());
          }),
        ),
      ),
    ),
  );

  onAccountsSitesLoaded$ = createEffect(() =>
    zip(
      this.actions$.pipe(ofType(ACCOUNTS_ACTIONS.getAccountsSuccess)),
      this.actions$.pipe(ofType(SITES_ACTIONS.getSitesSuccess)),
      this.store.select(APPLICATION_FILTERS_REDUCER['selectApp-filtersState']),
    ).pipe(
      first(),
      map(([{ payload: accounts }, { payload: sites }, filtersState]) => {
        const stateToUpdate = this.queryValidationService.getValidatedState(accounts, sites, filtersState);
        return APPLICATION_FILTERS_ACTIONS.validated({ data: stateToUpdate });
      }),
    ),
  );

  private onAccountsAndSitesQueriesGenerated(accountIdsHash: string, siteIdsHash: string): void {
    const queries = this.routerService.getCurrentUrlQueries();

    if (queries[APP_QUERY_PARAMS.accountIds] !== accountIdsHash || queries[APP_QUERY_PARAMS.siteIds] !== siteIdsHash) {
      this.routerService.navigateByAccountsAndSites(accountIdsHash, siteIdsHash);
    }
  }
}
